import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Loader, Save, Languages, Trash2, Plus } from 'lucide-react';
import { db } from '../../firebase/config';
import { createDefaultSiteContent, normalizeSiteContent, type LocalizedText, type SiteContentDoc } from '../../utils/siteContent';

const SITE_CONTENT_DOC = doc(db, 'siteContent', 'global');

type LanguageKey = 'zh' | 'en';

const emptyLocalizedText = (): LocalizedText => ({ zh: '', en: '' });

const cloneContent = (value: SiteContentDoc): SiteContentDoc => JSON.parse(JSON.stringify(value)) as SiteContentDoc;

const ContentTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState<SiteContentDoc>(createDefaultSiteContent());
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      const snapshot = await getDoc(SITE_CONTENT_DOC);
      if (snapshot.exists()) {
        setContent(normalizeSiteContent(snapshot.data() as Partial<SiteContentDoc>));
      } else {
        setContent(createDefaultSiteContent());
      }
      setLoading(false);
    };

    void fetchContent();
  }, []);

  const updateLocalizedText = (
    section: 'landing' | 'team' | 'contact',
    fieldPath: string,
    language: LanguageKey,
    value: string,
    index?: number,
  ) => {
    setContent((prev) => {
      const next = cloneContent(prev);

      if (section === 'landing') {
        const target = fieldPath.split('.').reduce<any>((acc, key) => acc[key], next.landing);
        target[language] = value;
        return next;
      }

      if (section === 'contact') {
        const target = fieldPath.split('.').reduce<any>((acc, key) => acc[key], next.contact);
        target[language] = value;
        return next;
      }

      if (section === 'team' && typeof index === 'number') {
        const target = fieldPath.split('.').reduce<any>((acc, key) => acc[key], next.team[index]);
        target[language] = value;
      }

      return next;
    });
  };

  const updateStringField = (path: string, value: string, index?: number) => {
    setContent((prev) => {
      const next = cloneContent(prev);

      if (path.startsWith('landing.')) {
        const keys = path.replace('landing.', '').split('.');
        const target = keys.slice(0, -1).reduce<any>((acc, key) => acc[key], next.landing as any);
        target[keys[keys.length - 1]] = value;
        return next;
      }

      if (path.startsWith('contact.')) {
        const keys = path.replace('contact.', '').split('.');
        if (keys[keys.length - 1] === 'addressLines') {
          const target = keys.slice(0, -1).reduce<any>((acc, key) => acc[key], next.contact as any);
          target[keys[keys.length - 1]] = value.split(/\r?\n/);
          return next;
        }

        const target = keys.slice(0, -1).reduce<any>((acc, key) => acc[key], next.contact as any);
        target[keys[keys.length - 1]] = value;
        return next;
      }

      if (path.startsWith('team.') && typeof index === 'number') {
        const keys = path.replace('team.', '').split('.');
        const target = keys.slice(0, -1).reduce<any>((acc, key) => acc[key], next.team[index] as any);
        target[keys[keys.length - 1]] = value;
      }

      if (path.startsWith('services.') && typeof index === 'number') {
        const keys = path.replace('services.', '').split('.');
        const target = keys.slice(0, -1).reduce<any>((acc, key) => acc[key], next.services[index] as any);
        target[keys[keys.length - 1]] = value;
      }

      return next;
    });
  };

  const copyLanguage = (sourceLang: LanguageKey, targetLang: LanguageKey) => {
    setContent((prev) => {
      const next = cloneContent(prev);

      const copyLocalized = (value: LocalizedText) => {
        value[targetLang] = value[sourceLang];
      };

      copyLocalized(next.landing.heroSubtitle);
      copyLocalized(next.landing.aboutTitle);
      copyLocalized(next.landing.aboutIntro);
      copyLocalized(next.landing.crossBorderTitle);
      next.landing.crossBorderParagraphs.forEach(copyLocalized);
      copyLocalized(next.landing.aboutImage.alt);
      copyLocalized(next.landing.aboutImage.caption);
      copyLocalized(next.landing.teamTitle);
      copyLocalized(next.landing.teamIntro);
      copyLocalized(next.contact.pageTitle);
      copyLocalized(next.contact.intro);
      copyLocalized(next.contact.firmName);
      copyLocalized(next.contact.torontoOffice.title);
      copyLocalized(next.contact.beijingOffice.title);
      copyLocalized(next.contact.footerTagline);

      next.team.forEach((member) => {
        copyLocalized(member.name);
        copyLocalized(member.imageAlt);
        copyLocalized(member.imageCaption);
        copyLocalized(member.summary);
      });

      return next;
    });
  };

  const addTeamMember = () => {
    setContent((prev) => ({
      ...prev,
      team: [
        ...prev.team,
        {
          id: `team-member-${prev.team.length + 1}`,
          name: emptyLocalizedText(),
          image: '',
          imageAlt: emptyLocalizedText(),
          imageCaption: emptyLocalizedText(),
          summary: emptyLocalizedText(),
          visible: true,
        },
      ],
    }));
  };

  const removeTeamMember = (index: number) => {
    setContent((prev) => ({
      ...prev,
      team: prev.team.filter((_, memberIndex) => memberIndex !== index),
    }));
  };

  const saveContent = async () => {
    setSaving(true);
    setStatusMessage('');
    try {
      await setDoc(SITE_CONTENT_DOC, content, { merge: true });
      setStatusMessage('Content saved successfully.');
    } finally {
      setSaving(false);
    }
  };

  const editorStyle = {
    display: 'grid',
    gap: '18px',
  };

  if (loading) {
    return <div style={{ padding: '24px' }}>Loading content editor...</div>;
  }

  return (
    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <Languages size={24} /> Content Management
          </h3>
          <p style={{ color: '#666', margin: 0 }}>Chinese is the default field. English fields are used as translation fallback when Chinese is empty.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => copyLanguage('en', 'zh')} className="btn" style={{ border: '1px solid #ddd' }}>Copy English to Chinese</button>
          <button onClick={() => copyLanguage('zh', 'en')} className="btn" style={{ border: '1px solid #ddd' }}>Copy Chinese to English</button>
          <button onClick={saveContent} className="btn btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
            {saving ? 'Saving...' : 'Save Content'}
          </button>
        </div>
      </div>

      {statusMessage && (
        <div style={{ marginBottom: '20px', padding: '12px 14px', borderRadius: '8px', backgroundColor: '#eef8f1', color: '#2e7d32', border: '1px solid #cfe9d3' }}>{statusMessage}</div>
      )}

      <div style={{ display: 'grid', gap: '28px' }}>
        <section style={{ padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
          <h4 style={{ marginTop: 0, marginBottom: '18px' }}>Landing Page</h4>
          <div style={editorStyle}>
            <LocalizedEditor label="Hero subtitle" value={content.landing.heroSubtitle} onChange={(lang, value) => updateLocalizedText('landing', 'heroSubtitle', lang, value)} />
            <LocalizedEditor label="About title" value={content.landing.aboutTitle} onChange={(lang, value) => updateLocalizedText('landing', 'aboutTitle', lang, value)} />
            <LocalizedEditor label="About intro" value={content.landing.aboutIntro} multiline onChange={(lang, value) => updateLocalizedText('landing', 'aboutIntro', lang, value)} />
            <LocalizedEditor label="Cross-border title" value={content.landing.crossBorderTitle} onChange={(lang, value) => updateLocalizedText('landing', 'crossBorderTitle', lang, value)} />
            {content.landing.crossBorderParagraphs.map((paragraph, index) => (
              <LocalizedEditor
                key={`cross-border-${index}`}
                label={`Cross-border paragraph ${index + 1}`}
                value={paragraph}
                multiline
                onChange={(lang, value) => updateLocalizedText('landing', `crossBorderParagraphs.${index}`, lang, value, index)}
              />
            ))}
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ fontWeight: 600 }}>About image</div>
              <label>
                Image URL
                <input
                  value={content.landing.aboutImage.src}
                  onChange={(event) => updateStringField('landing.aboutImage.src', event.target.value)}
                  style={inputStyle}
                />
              </label>
              <button type="button" className="btn" style={{ width: 'fit-content', border: '1px solid #ddd' }} onClick={() => updateStringField('landing.aboutImage.src', '')}>Remove image</button>
              <LocalizedEditor label="Image alt" value={content.landing.aboutImage.alt} onChange={(lang, value) => updateLocalizedText('landing', 'aboutImage.alt', lang, value)} />
              <LocalizedEditor label="Image caption" value={content.landing.aboutImage.caption} multiline onChange={(lang, value) => updateLocalizedText('landing', 'aboutImage.caption', lang, value)} />
              <LocalizedEditor label="Team title" value={content.landing.teamTitle} onChange={(lang, value) => updateLocalizedText('landing', 'teamTitle', lang, value)} />
              <LocalizedEditor label="Team intro" value={content.landing.teamIntro} multiline onChange={(lang, value) => updateLocalizedText('landing', 'teamIntro', lang, value)} />
            </div>
          </div>
        </section>

        <section style={{ padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h4 style={{ marginTop: 0, marginBottom: '18px' }}>Our Team</h4>
            <button type="button" onClick={addTeamMember} className="btn" style={{ border: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={16} /> Add team member
            </button>
          </div>
          <div style={{ display: 'grid', gap: '20px' }}>
            {content.team.map((member, index) => (
              <article key={member.id} style={{ padding: '18px', border: '1px solid #eee', borderRadius: '12px', backgroundColor: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <strong>{member.id}</strong>
                  <button type="button" onClick={() => removeTeamMember(index)} className="btn" style={{ border: '1px solid #ddd', color: '#b42318', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Trash2 size={16} /> Remove member
                  </button>
                </div>
                <div style={editorStyle}>
                  <LocalizedEditor label="Name" value={member.name} onChange={(lang, value) => updateLocalizedText('team', 'name', lang, value, index)} />
                  <label>
                    Image URL
                    <input
                      value={member.image}
                      onChange={(event) => updateStringField('team.image', event.target.value, index)}
                      style={inputStyle}
                    />
                  </label>
                  <button type="button" className="btn" style={{ width: 'fit-content', border: '1px solid #ddd' }} onClick={() => updateStringField('team.image', '', index)}>Remove image</button>
                  <LocalizedEditor label="Image alt" value={member.imageAlt} onChange={(lang, value) => updateLocalizedText('team', 'imageAlt', lang, value, index)} />
                  <LocalizedEditor label="Image caption" value={member.imageCaption} multiline onChange={(lang, value) => updateLocalizedText('team', 'imageCaption', lang, value, index)} />
                  <LocalizedEditor label="Summary" value={member.summary} multiline onChange={(lang, value) => updateLocalizedText('team', 'summary', lang, value, index)} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={member.visible}
                      onChange={(event) => setContent((prev) => {
                        const next = cloneContent(prev);
                        next.team[index].visible = event.target.checked;
                        return next;
                      })}
                    />
                    Visible on the public team section
                  </label>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section style={{ padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
          <h4 style={{ marginTop: 0, marginBottom: '18px' }}>Services</h4>
          <div style={{ display: 'grid', gap: '18px' }}>
            {content.services.map((service, index) => (
              <article key={service.id} style={{ padding: '18px', border: '1px solid #eee', borderRadius: '12px', backgroundColor: '#fafafa', display: 'grid', gap: '12px' }}>
                <strong>{service.id}</strong>
                <label>
                  Card title
                  <input
                    value={service.cardTitle}
                    onChange={(event) => updateStringField('services.cardTitle', event.target.value, index)}
                    style={inputStyle}
                  />
                </label>
                <label>
                  Card description
                  <textarea
                    value={service.cardDescription}
                    onChange={(event) => updateStringField('services.cardDescription', event.target.value, index)}
                    rows={3}
                    style={textareaStyle}
                  />
                </label>
                <label>
                  Service page title (H1)
                  <input
                    value={service.pageTitle}
                    onChange={(event) => updateStringField('services.pageTitle', event.target.value, index)}
                    style={inputStyle}
                  />
                </label>
                <label>
                  Service page intro
                  <textarea
                    value={service.pageDescription}
                    onChange={(event) => updateStringField('services.pageDescription', event.target.value, index)}
                    rows={3}
                    style={textareaStyle}
                  />
                </label>
                <label>
                  Service page full content
                  <textarea
                    value={service.pageContent}
                    onChange={(event) => updateStringField('services.pageContent', event.target.value, index)}
                    rows={8}
                    style={textareaStyle}
                  />
                </label>
                <label>
                  Features (one bullet per line)
                  <textarea
                    value={service.featureList}
                    onChange={(event) => updateStringField('services.featureList', event.target.value, index)}
                    rows={6}
                    style={textareaStyle}
                  />
                </label>
                <label>
                  Hero image URL
                  <input
                    value={service.image}
                    onChange={(event) => updateStringField('services.image', event.target.value, index)}
                    style={inputStyle}
                  />
                </label>
                <label>
                  CTA button text
                  <input
                    value={service.ctaText}
                    onChange={(event) => updateStringField('services.ctaText', event.target.value, index)}
                    style={inputStyle}
                  />
                </label>
              </article>
            ))}
          </div>
        </section>

        <section style={{ padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
          <h4 style={{ marginTop: 0, marginBottom: '18px' }}>Contact Information</h4>
          <div style={editorStyle}>
            <LocalizedEditor label="Page title" value={content.contact.pageTitle} onChange={(lang, value) => updateLocalizedText('contact', 'pageTitle', lang, value)} />
            <LocalizedEditor label="Intro" value={content.contact.intro} multiline onChange={(lang, value) => updateLocalizedText('contact', 'intro', lang, value)} />
            <LocalizedEditor label="Firm name" value={content.contact.firmName} onChange={(lang, value) => updateLocalizedText('contact', 'firmName', lang, value)} />
            <LocalizedEditor label="Toronto office title" value={content.contact.torontoOffice.title} onChange={(lang, value) => updateLocalizedText('contact', 'torontoOffice.title', lang, value)} />
            <label>
              Toronto address lines
              <textarea
                value={content.contact.torontoOffice.addressLines.join('\n')}
                onChange={(event) => updateStringField('contact.torontoOffice.addressLines', event.target.value)}
                rows={3}
                style={textareaStyle}
              />
            </label>
            <label>
              Toronto phone
              <input
                value={content.contact.torontoOffice.phone || ''}
                onChange={(event) => updateStringField('contact.torontoOffice.phone', event.target.value)}
                style={inputStyle}
              />
            </label>
            <label>
              Toronto fax
              <input
                value={content.contact.torontoOffice.fax || ''}
                onChange={(event) => updateStringField('contact.torontoOffice.fax', event.target.value)}
                style={inputStyle}
              />
            </label>
            <label>
              Toronto email
              <input
                value={content.contact.torontoOffice.email || ''}
                onChange={(event) => updateStringField('contact.torontoOffice.email', event.target.value)}
                style={inputStyle}
              />
            </label>
            <LocalizedEditor label="Beijing office title" value={content.contact.beijingOffice.title} onChange={(lang, value) => updateLocalizedText('contact', 'beijingOffice.title', lang, value)} />
            <label>
              Beijing address lines
              <textarea
                value={content.contact.beijingOffice.addressLines.join('\n')}
                onChange={(event) => updateStringField('contact.beijingOffice.addressLines', event.target.value)}
                rows={3}
                style={textareaStyle}
              />
            </label>
            <label>
              Beijing phone
              <input
                value={content.contact.beijingOffice.phone || ''}
                onChange={(event) => updateStringField('contact.beijingOffice.phone', event.target.value)}
                style={inputStyle}
              />
            </label>
            <LocalizedEditor label="Footer tagline" value={content.contact.footerTagline} multiline onChange={(lang, value) => updateLocalizedText('contact', 'footerTagline', lang, value)} />
          </div>
        </section>
      </div>
    </div>
  );
};

const LocalizedEditor: React.FC<{
  label: string;
  value: LocalizedText;
  multiline?: boolean;
  onChange: (language: LanguageKey, value: string) => void;
}> = ({ label, value, multiline, onChange }) => {
  const inputProps = {
    style: multiline ? textareaStyle : inputStyle,
  };

  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <label>
          Chinese (default)
          {multiline ? (
            <textarea value={value.zh} onChange={(event) => onChange('zh', event.target.value)} rows={4} {...inputProps} />
          ) : (
            <input value={value.zh} onChange={(event) => onChange('zh', event.target.value)} {...inputProps} />
          )}
        </label>
        <label>
          English translation
          {multiline ? (
            <textarea value={value.en} onChange={(event) => onChange('en', event.target.value)} rows={4} {...inputProps} />
          ) : (
            <input value={value.en} onChange={(event) => onChange('en', event.target.value)} {...inputProps} />
          )}
        </label>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '6px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '10px',
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '110px',
};

export default ContentTab;
